<template>
	<header class="c-employee-page-header">
		<div class="flex flex-col >=768:flex-row">
			<div
				v-if="intro"
				class="
					c-employee-page-header__main
					relative
					px-layout-margin
					pt-site-header
					text-text
					pb-sm/v
				"
			>
				<!-- Slot things in -->
				<div
					v-if="$scopedSlots.top || computedAreas"
					class="mb-64 >=1024:mb-24"
				>
					<slot name="top">
						<div class="flex flex-wrap items-center gap-8">
							<AreaReferences
								v-if="computedAreas"
								class="hidden >=768:contents"
								:items="computedAreas"
								:show-max="3"
							/>
						</div>
					</slot>
				</div>
				<div v-else class="pt-48 >=768:hidden"></div>

				<!-- Heading -->
				<BaseH1
					v-if="intro.title"
					class="w-full >=768:w-6/12col break-words"
					:balance="false"
					v-html="intro.title"
				/>

				<!-- Teaser -->
				<div
					v-if="details"
					class="
						mt-20
						>=768:mt-xs/v
						w-full
						>=768:w-6/12col
						break-words
					"
				>
					<p
						v-if="details.title && details.title.length"
						class="
							block
							text-teaser
							whitespace-pre-wrap
							<768:mb-2xs/v
						"
						v-text="details.title.join(', ')"
					></p>

					<!-- Slot things in right after the teaser -->
					<slot
						v-if="$scopedSlots.afterTeaser"
						name="afterTeaser"
					></slot>
				</div>

				<div
					class="mt-sm/v mb-5xs/v >=768:mb-0"
					:class="{
						'>=768:mt-2xl/v': computedCompetencies,
					}"
				>
					<AreaReferences
						v-if="computedAreas"
						class=">=768:hidden -mx-2"
						:items="computedAreas"
						:show-max="3"
					/>

					<div
						v-if="computedCompetencies"
						class="align-baseline text-red text-body-xs font-medium"
						:class="{
							'mt-8': computedAreas,
						}"
					>
						<h2
							v-if="computedCompetencies.label"
							class="inline uppercase"
							v-text="computedCompetencies.label + ': '"
						></h2>
						<ul class="contents">
							<li
								v-for="(
									item, index
								) in computedCompetencies.items"
								:key="index"
								class="inline"
								v-text="
									index <
									computedCompetencies.items.length - 1
										? item + ', '
										: item
								"
							></li>
						</ul>
					</div>
				</div>
			</div>

			<!-- Image -->
			<figure
				v-if="intro && intro.image"
				class="
					flex flex-col
					justify-center
					>=768:items-center
					items-end
					bg-gray-light
					w-full
					relative
				"
			>
				<div
					class="
						bg-gray
						>=768:w-4/12col
						w-7/12col
						>=768:max-h-310
						h-184
						absolute
						>=768:top-0
						bottom-0
						<768:left-0 <768:ml-layout-margin
						my-layout-margin
						>=768:-ml-1/2
					"
				></div>
				<BaseImg
					class="
						w-310
						h-310
						<768:w-184 <768:h-184 <768:mr-layout-margin
					"
					:src="intro.image.cropUrl || intro.image.url"
					:alt="intro.image.altText"
					:ratio="1 / 1"
					:img-attrs="{
						fetchpriority: 'high',
						class: 'rounded-full',
					}"
					:credits="intro.image.credits"
					:quality="80"
				/>
				<figcaption
					v-if="intro.imageText"
					class="
						w-full
						px-layout-margin
						py-2xs/v
						text-body-xs text-gray-darker
					"
					v-text="intro.imageText"
				></figcaption>
			</figure>
		</div>

		<MetaInfoBar
			:details="details"
			:english-layout="englishLayout"
		></MetaInfoBar>
	</header>
</template>

<script>
import AreaReferences from '~/components/main/AreaReferences';
import MetaInfoBar from '~/components/main/MetaInfoBar';
import { nuxtImgSizes } from '~/assets/js/utilities/helpers';

export default {
	name: 'EmployeePageHeader',

	components: {
		AreaReferences,
		MetaInfoBar,
	},

	props: {
		intro: {
			type: Object,
			default: () => ({}),
		},
		headerGraphic: {
			type: Number,
			default: -2,
		},
		details: {
			type: Object,
			default: () => ({}),
		},
		englishLayout: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		computedAreas() {
			return this.details?.areas?.length ? this.details.areas : null;
		},

		computedCompetencies() {
			return this.details?.competencies?.items?.length
				? this.details.competencies
				: null;
		},
	},

	methods: {
		nuxtImgSizes,
	},
};
</script>

<style lang="postcss">
.c-employee-page-header__main:before {
	@apply absolute bg-gray-light;
	content: '';
	left: calc(var(--margin, 0px) * -1);
	top: 0;
	width: calc(100% + var(--margin, 0px) * 2);
	height: 100%;
	z-index: -1;
}
@screen >=1441 {
	.c-employee-page-header__main {
		--margin: (
				var(--visual-viewport-width, 100vw) -
					var(--theme-layout-max, 1440px)
			) / 2;
	}
}

@screen print {
	.c-employee-page-header,
	.c-employee-page-header__main > * {
		@apply text-text;
	}
	.c-employee-page-header__main {
		@apply contents;

		&:before {
			@apply hidden;
		}
	}
	.c-employee-page-header__header-graphic {
		@apply hidden;
	}
}
</style>
